/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */

'use-strict';

import { IScope } from 'angular';
import { EFileSection } from '../../../src/app/@shared-types/misc/file';
import { FileInput } from '../../../src/app/@types';
import { SalesAPIService } from '../../../shared/api/sales-api.service';
import { NumberConversionService } from '../../../shared/converter/number-converter.service';
import { FileService } from '../../../shared/storage/file.service';
import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import { VehicleHandoverRequest } from '../../../src/app/@shared-types/vehicle/availability';
import { ConfigService } from '../../../shared/config/config.service';
import { SurveyAPIService } from '../../../shared/api/survey.service';
import { ESurveyState } from '../../../src/app/@shared-types/surveys/enums';
import { SurveyRequest } from '../../../src/app/@shared-types/surveys';
import { VehicleFormComponentExitFunction } from '../../../src/app/@types/vehicle';
import { TierService } from '../../tenant/tier.service';
import { EFeatures } from '../../../src/app/@shared-types/tenant/enums';
import { CustomerModel } from '../../../src/app/shared/models/customer/customer';

interface HandoverScope extends IScope {
  sending: boolean,
  exitForm: () => void,
  exit: (data: VehicleFormComponentExitFunction) => void,
  formTransmission: () => void,
  vehicle: AutoboardVehicle,
  newVehicleData: {
    handoverDate: string | null,
    invoiceDate: string | null,
    handoverImage?: FileInput,
    customer: string,
    email: string,
  },
  locations: { [key: string]: string },
  survey: {
    display: boolean,
    enabled: boolean,
    sendDays: number,
    disabled: boolean,
  },
  toggleSurvey: () => void,
}

/* @ngInject */
export default function HandoverFormController(
  $scope: HandoverScope,
  fileService: FileService,
  salesApiService: SalesAPIService,
  numberConversionService: NumberConversionService,
  configService: ConfigService,
  surveyApiService: SurveyAPIService,
  tierService: TierService,
  customerModel: CustomerModel,
): void {
  $scope.sending = false;

  $scope.newVehicleData = {
    handoverDate: null,
    handoverImage: undefined,
    invoiceDate: null,
    customer: '',
    email: '',
  };

  $scope.survey = {
    display: true,
    enabled: configService.get('survey_sending_enabled') as boolean && tierService.authorizedToUseFeature(EFeatures.SURVEY_ADD),
    sendDays: configService.get('survey_sending_days') as number,
    disabled: false,
  };

  $scope.toggleSurvey = (): void => {
    if ($scope.survey.display) {
      $scope.survey.display = false;
      return;
    }

    $scope.survey.display = true;
  };

  const getCustomerData = async (): Promise<void> => {
    const customerId = $scope.vehicle.vehicleData.availability.sellData?.customerId;
    if (!customerId) return;

    const customer = await customerModel.get(customerId);
    $scope.newVehicleData.customer = `${customer.firstName} ${customer.lastName}`;
    $scope.newVehicleData.email = customer.email;
  };
  getCustomerData();

  const sendSurvey = (skipped?: boolean): Promise<void> => {
    const surveyRequest: SurveyRequest = {
      customerEmail: $scope.newVehicleData.email,
      customerName: $scope.newVehicleData.customer,
      vehicleId: String($scope.vehicle._id),
      handoverDate: numberConversionService.parseDate($scope.newVehicleData.handoverDate),
    };

    if (skipped) {
      surveyRequest.customerEmail = 'N/A';
      surveyRequest.customerName = $scope.vehicle.vehicleData.availability.sellData?.customer || 'N/A';
      surveyRequest.state = ESurveyState.NONE_SENT;
    }

    return surveyApiService.postSurvey(surveyRequest);
  };

  $scope.exitForm = () => $scope.exit({ removeLock: true });

  $scope.formTransmission = async () => {
    $scope.sending = true;

    const handoverRequest: VehicleHandoverRequest = {
      vehicleId: String($scope.vehicle._id),
      handoverDate: numberConversionService.frontendStringToDate($scope.newVehicleData.handoverDate),
      invoiceDate: numberConversionService.frontendStringToDate($scope.newVehicleData.invoiceDate),
    };

    const postPromises: Promise<unknown>[] = [];

    if ($scope.newVehicleData.handoverImage && $scope.newVehicleData.handoverImage.file) {
      await fileService.uploadContextualFile(
        $scope.newVehicleData.handoverImage.file,
        $scope.newVehicleData.handoverImage.progressBar,
        {
          identifier: String($scope.vehicle._id),
          section: EFileSection.VEHICLE,
          type: $scope.newVehicleData.handoverImage.fileType,
        },
      );
    }

    postPromises.push(salesApiService.postHandover(handoverRequest));
    if ($scope.survey.enabled && $scope.survey.display) {
      postPromises.push(sendSurvey());
    } else if ($scope.survey.enabled && !$scope.survey.display) {
      // Send skipped survey, if seller decided not to send survey
      postPromises.push(sendSurvey(true));
    }

    await Promise.all(postPromises);
    return $scope.exit({});
  };
}
